import { UnknownAction } from '@reduxjs/toolkit';
import { getModel } from '@smx-ui-model/store-model';
import { GenericModelSchemaType, SchemaModelFieldType, StoreModelType, TableResourceType } from '@smx-ui-model/types';
import { Empty, Form, Table, TablePaginationConfig } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { EntityTableLoaderForm } from './entity-table-loader-form';
import { EntityTableRow } from './entity-table-row';
import { useCommitFromMutation } from './hook/use-commit-from-mutation';

interface TableEntityBaseProps<C> {
    entityName: keyof StoreModelType;
    dataSource: any;
    isCheckBoxColumn?: boolean;
    onSelectedRow?: (selectedRows: string[]) => void;
    setCurrentModel?: (model: TableResourceType) => UnknownAction;
    pagination?: TablePaginationConfig;
    setPagination?: React.Dispatch<React.SetStateAction<TablePaginationConfig>>;
    setFetchKey?: React.Dispatch<React.SetStateAction<number>>;
    isLoading?: boolean;
    contextName?: C;
}

export const TableEntityBase = forwardRef(
    <C,>(
        {
            entityName,
            dataSource,
            isCheckBoxColumn,
            onSelectedRow,
            setCurrentModel,
            pagination,
            setPagination,
            isLoading = false,
            contextName,
            setFetchKey,
        }: TableEntityBaseProps<C>,
        ref: React.Ref<any>,
    ) => {
        const dispatch = useDispatch();
        const [form] = Form.useForm();
        // const environment = useRelayEnvironment();
        const values = Form.useWatch([], { form, preserve: true });
        const model = getModel<GenericModelSchemaType>(entityName);
        const changedValuesRef = useRef<{ [key: string]: { [key: string]: string } }>({});
        const valuesBeforeChangedRef = useRef<{ [key: string]: { [key: string]: string } }>({});

        const [selectedRows, setSelectedRows] = useState<string[]>([]);
        const selectedRowsRef = useRef<string[]>([]);

        const bulkUpdating = useCommitFromMutation(model.mutation?.bulkUpdate);

        useEffect(() => {
            if (bulkUpdating.statusUpdate === 'success') {
                changedValuesRef.current = {};
                setFetchKey?.((prevValue) => prevValue + 1);
            }
        }, [bulkUpdating.statusUpdate]);

        const bulkSave = () => {
            form.submit();
        };

        useImperativeHandle(ref, () => ({
            submitTableChanges: bulkSave,
            changedValues: bulkUpdating.statusUpdate === 'success' ? {} : changedValuesRef.current,
            isInFlight: bulkUpdating.isInFlightUpdate,
        }));

        useEffect(() => {
            const listKey = Object.keys(values || {});
            if (listKey.length > 0 && listKey.length > Object.keys(valuesBeforeChangedRef.current || {}).length) {
                valuesBeforeChangedRef.current = {
                    ...(valuesBeforeChangedRef.current || {}),
                    ...values,
                };
            }
        }, [values]);

        useEffect(() => {
            onSelectedRow?.(selectedRows);
        }, [selectedRows]);

        selectedRowsRef.current = selectedRows;
        const listColumnName = contextName ? model.context?.[contextName] || [] : model.schema.context.table;

        const columns = listColumnName.map((key) => {
            const field = (model.schema.field as any)[key] as SchemaModelFieldType;
            return {
                title: contextName ? model.aliasLabelColumn?.[contextName]?.[field.name] : field.title,
                dataIndex: field.title,
                key: key as string,
            };
        });

        const handlerOnRow = (record: TableResourceType) => {
            const handleCLick = () => {
                console.log('click on row', record);
                // const gg = environment?.getStore().getSource().get(record.key);
                // console.log(gg, (gg as any).__typename.toLowerCase());

                if (setCurrentModel) {
                    dispatch(setCurrentModel(record));
                }

                // setSelectNode(record.userRef);
            };

            return {
                onClick: handleCLick,
            };
        };

        const rowSelection = isCheckBoxColumn
            ? {
                  onChange: (selectedRowKeys: React.Key[], selectedRows: TableResourceType[]) => {
                      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
                      setSelectedRows(selectedRowKeys as string[]);
                  },
                  type: 'checkbox',
                  selectedRowKeys: selectedRows,
              }
            : undefined;

        const handleTableChange = (pagination: TablePaginationConfig) => {
            setPagination?.(pagination);
        };

        const tableComponents = useMemo(
            () => ({
                body: {
                    row: ({ children, ...props }: { children: any }) => {
                        const dataRowKey = (props as any)['data-row-key'];
                        const record = dataRowKey ? dataSource?.find((item: any) => item.key === dataRowKey) : null;

                        return record ? (
                            <>
                                <EntityTableLoaderForm
                                    node={record?.userRef}
                                    contextName={contextName}
                                    modelName={entityName}
                                />
                                <EntityTableRow<C>
                                    {...props}
                                    node={record?.userRef}
                                    modelName={entityName}
                                    isCheckBoxColumn={isCheckBoxColumn}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRowsRef.current}
                                    contextName={contextName}
                                />
                            </>
                        ) : (
                            <tr style={{ width: '200px' }}>
                                <td colSpan={100}>
                                    <Empty />
                                </td>
                            </tr>
                        );
                    },
                },
            }),
            [dataSource, entityName, isCheckBoxColumn, setSelectedRows, contextName],
        );

        const table = (
            <Table<TableResourceType>
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                bordered
                size="small"
                loading={isLoading}
                components={tableComponents}
                rowSelection={rowSelection as any}
                onRow={handlerOnRow}
            />
        );

        return model.context?.tableInput ? (
            <>
                <Form
                    form={form}
                    onFinish={() => {
                        bulkUpdating.updateCommit(changedValuesRef.current);
                    }}
                    onValuesChange={(changedValues, allValues) => {
                        const [key] = Object.keys(changedValues);

                        if (!changedValuesRef.current) {
                            valuesBeforeChangedRef.current = {
                                ...allValues,
                            };
                        }

                        changedValuesRef.current = {
                            ...(changedValuesRef.current || {}),
                            [key]: {
                                ...(changedValuesRef.current?.[key] || {}),
                                ...changedValues[key],
                            },
                        };
                    }}
                >
                    {table}
                </Form>
            </>
        ) : (
            table
        );
    },
) as <C>(props: TableEntityBaseProps<C> & { ref?: React.Ref<any> }) => React.ReactElement;
